import React, { useState, useEffect, type ChangeEvent, type FormEvent } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS from "../../api/apiConfig";
import "../../assets/css/theme.css";
import "../../assets/css/cart.css";
import type { Address, CityItem, StateItem } from "../../types";

const ADDRESS_TYPE_OPTIONS = [
  { value: "home", label: "Home" },
  { value: "office", label: "Office" },
  { value: "other", label: "Other" },
];

interface CheckoutProps {
  addressOnly?: boolean;
  editAddress?: Address | null;
  onAddressAdded?: () => void;
  onCancel?: () => void;
}

interface AddressFormData {
  full_name: string;
  contact_no: string;
  email: string;
  address_line: string;
  state_id: string | number;
  city_id: string | number;
  postal_code: string;
  address_type: string;
  is_default_shipping: boolean;
  is_default_billing: boolean;
  /** only sent when editing an existing address */
  address_id?: number;
}

const Checkout = ({
  addressOnly = false,
  editAddress = null,
  onAddressAdded,
  onCancel,
}: CheckoutProps) => {
  const [formData, setFormData] = useState<AddressFormData>({
    full_name: "",
    contact_no: "",
    email: "",
    address_line: "",
    state_id: "",
    city_id: "",
    postal_code: "",
    address_type: "",
    is_default_shipping: true,
    is_default_billing: false,
  });
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [states, setStates] = useState<StateItem[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState<string>("");
  const [cities, setCities] = useState<CityItem[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityDisabled, setCityDisabled] = useState(true);
  const [apiError, setApiError] = useState<string>("");
  const navigate = useNavigate();

  // Populate form for edit mode
  useEffect(() => {
    if (editAddress) {
      setFormData({
        full_name: editAddress.full_name || "",
        contact_no: editAddress.contact_no || "",
        email: editAddress.email || "",
        address_line: editAddress.address_line || "",
        state_id: editAddress.state_id || "",
        city_id: editAddress.city_id || "",
        postal_code: editAddress.postal_code || "",
        address_type: editAddress.address_type || "",
        is_default_shipping: !!editAddress.is_default_shipping,
        is_default_billing: !!editAddress.is_default_billing,
      });
    }
  }, [editAddress]);

  // Fetch states from API on mount (with access_token)
  useEffect(() => {
    setStatesLoading(true);
    setStatesError("");
    const token = localStorage.getItem("authToken");
    if (!token) {
      setStatesError("You must be logged in to select a state.");
      setStatesLoading(false);
      return;
    }
    fetch(API_ENDPOINTS.GET_STATES, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.states)) {
          setStates(data.states);
          localStorage.setItem("statesList", JSON.stringify(data.states));
        } else {
          setStates([]);
          setStatesError("Failed to load states.");
          localStorage.removeItem("statesList");
        }
        setStatesLoading(false);
      })
      .catch(() => {
        setStates([]);
        setStatesError("Failed to load states.");
        setStatesLoading(false);
        localStorage.removeItem("statesList");
      });
  }, []);

  // Fetch cities when state changes (with access_token)
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (formData.state_id && token) {
      setCityDisabled(true);
      setCityLoading(true);
      const cityUrl = `${API_ENDPOINTS.GET_CITY}?state_id=${formData.state_id}`;
      fetch(cityUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          let cityList: CityItem[] = [];
          if (Array.isArray(data.cities)) {
            cityList = data.cities;
          } else if (Array.isArray(data.data)) {
            cityList = data.data;
          }
          setCities(cityList);
          localStorage.setItem("citiesList", JSON.stringify(cityList));
          setCityDisabled(false);
          setCityLoading(false);
        })
        .catch(() => {
          setCities([]);
          setCityDisabled(false);
          setCityLoading(false);
          localStorage.removeItem("citiesList");
        });
    } else {
      setCities([]);
      setCityDisabled(true);
      localStorage.removeItem("citiesList");
    }
    // Reset city selection if state changes
    setFormData((prev) => ({ ...prev, city_id: "" }));
    // eslint-disable-next-line
  }, [formData.state_id]);

  // Fix: After cities are loaded in edit mode, ensure city_id is set so dropdown shows correct city
  useEffect(() => {
    if (
      editAddress &&
      editAddress.city_id &&
      cities.length > 0 &&
      formData.city_id !== editAddress.city_id
    ) {
      setFormData((prev) => ({
        ...prev,
        city_id: editAddress.city_id,
      }));
    }
    // eslint-disable-next-line
  }, [cities, editAddress]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");
    setShowConfirm(false);

    // Prepare payload for API
    const payload = { ...formData };

    // If editing, include address_id (if your backend supports it for create)
    if (editAddress && editAddress.address_id) {
      payload.address_id = editAddress.address_id;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setApiError("You must be logged in to save address.");
      toast.error("You must be logged in to save address.", { position: "top-right", autoClose: 2000 });
      return;
    }

    try {
      const res = await fetch(
        API_ENDPOINTS.CREATE_USER_ADDRESS,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (res.ok && (data.status || data.success)) {
        setShowConfirm(true);
        toast.success("Address saved successfully!", { position: "top-right", autoClose: 2000 });
        setTimeout(() => {
          setShowConfirm(false);
          if (onAddressAdded) onAddressAdded();
        }, 1200);
      } else {
        setApiError(data.message || "Failed to save address.");
        toast.error(data.message || "Failed to save address.", { position: "top-right", autoClose: 2000 });
      }
    } catch (err) {
      setApiError("Failed to save address.");
      toast.error("Failed to save address.", { position: "top-right", autoClose: 2000 });
    }
  };

  // For addressOnly mode, show only address fields
  if (addressOnly) {
    const canSubmit =
      !showConfirm &&
      !!formData.full_name &&
      !!formData.contact_no &&
      !!formData.email &&
      !!formData.address_line &&
      !!formData.city_id &&
      !!formData.state_id &&
      !!formData.postal_code &&
      !!formData.address_type;

    return (
      <>
        <form onSubmit={handleSubmit}>
          <div className="ffc-form-grid">
            <div className="ffc-field">
              <label className="ffc-label" htmlFor="ffc-addr-name">Full name</label>
              <input
                id="ffc-addr-name"
                type="text"
                className="ffc-input"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ffc-field">
              <label className="ffc-label" htmlFor="ffc-addr-phone">Contact no</label>
              <input
                id="ffc-addr-phone"
                type="tel"
                inputMode="tel"
                className="ffc-input"
                name="contact_no"
                value={formData.contact_no}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ffc-field ffc-field--wide">
              <label className="ffc-label" htmlFor="ffc-addr-email">Email</label>
              <input
                id="ffc-addr-email"
                type="email"
                className="ffc-input"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ffc-field ffc-field--wide">
              <label className="ffc-label" htmlFor="ffc-addr-line">Address line</label>
              <input
                id="ffc-addr-line"
                type="text"
                className="ffc-input"
                name="address_line"
                placeholder="House / flat / street"
                value={formData.address_line}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ffc-field">
              <label className="ffc-label" htmlFor="ffc-addr-state">State</label>
              <select
                id="ffc-addr-state"
                className="ffc-select-input"
                name="state_id"
                value={formData.state_id}
                onChange={handleInputChange}
                required
                disabled={statesLoading}
              >
                <option value="">
                  {statesLoading ? "Loading states..." : "Select state"}
                </option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_id}>
                    {state.state_name}
                  </option>
                ))}
              </select>
              {statesError && <div className="ffc-field-error">{statesError}</div>}
            </div>

            <div className="ffc-field">
              <label className="ffc-label" htmlFor="ffc-addr-city">City</label>
              <select
                id="ffc-addr-city"
                className="ffc-select-input"
                name="city_id"
                value={formData.city_id}
                onChange={handleInputChange}
                required
                disabled={cityDisabled}
              >
                <option value="">
                  {cityLoading ? "Loading cities..." : "Select city"}
                </option>
                {cities.map((city) => (
                  <option key={city.city_id} value={city.city_id}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ffc-field">
              <label className="ffc-label" htmlFor="ffc-addr-zip">ZIP code</label>
              <input
                id="ffc-addr-zip"
                type="text"
                inputMode="numeric"
                className="ffc-input"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ffc-field">
              <span className="ffc-label" id="ffc-addr-type-label">Address type</span>
              <div className="ffc-pills" role="radiogroup" aria-labelledby="ffc-addr-type-label">
                {ADDRESS_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={formData.address_type === opt.value}
                    className={`ffc-pill${formData.address_type === opt.value ? " is-active" : ""}`}
                    onClick={() => setFormData((prev) => ({ ...prev, address_type: opt.value }))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {apiError && <div className="ffc-form-alert" role="alert">{apiError}</div>}

          <div className="ffc-form-actions">
            {onCancel && (
              <button type="button" className="ffh-btn ffh-btn--outline" onClick={onCancel}>
                Cancel
              </button>
            )}
            <button type="submit" className="ffh-btn" disabled={!canSubmit}>
              {editAddress ? "Update address" : "Save address"}
            </button>
            {showConfirm && <span className="ffc-saved">✓ Address saved!</span>}
          </div>
        </form>
        <ToastContainer />
      </>
    );
  }

  // ... (keep your original checkout form for non-addressOnly mode)
  // If you want to disable the original checkout form, you can return null here.
  return null;
};

export default Checkout;